import { cn } from "./classNames-DcGnTh2N.js";
import { TextInput } from "./TextInput-K2DSDs5O.js";
import { ColorInput } from "./ColorInput-CDKH8q_f.js";
import { HelpBlock } from "./internal/components/HelpBlock.js";
import { NumberInput } from "./internal/blocks/NumberInput.js";
import { Textarea } from "./internal/blocks/Textarea.js";
import { createContext, use, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/blocks/FormGroup/context.ts
const FormGroupContext = createContext({ state: "default" });
const FormGroupProvider = FormGroupContext;
const useFormGroupContext = () => use(FormGroupContext);
//#endregion
//#region src/blocks/FormGroup/FormGroupColorInput.tsx
const FormGroupColorInput = ({ ref, ...props }) => {
	const { state } = useFormGroupContext();
	return /* @__PURE__ */ jsx(ColorInput, {
		state,
		...props,
		ref
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroupError.tsx
const toMessage = (entry) => typeof entry === "string" ? entry : entry?.message;
/**
* Field validation error.
*
* - `role="alert"` announces it even when focus is elsewhere (e.g. on submit).
* - A `<div>`, not `<p>`, so several errors can render as a `<ul>`.
*/
const FormGroupError = ({ children, className, errors, ...rest }) => {
	const content = useMemo(() => {
		if (children) return children;
		const messages = [...new Set((errors ?? []).map(toMessage).filter((message) => !!message))];
		if (messages.length === 0) return null;
		if (messages.length === 1) return messages[0];
		return /* @__PURE__ */ jsx("ul", {
			className: "tw:m-0 tw:flex tw:list-disc tw:flex-col tw:gap-0 tw:pl-4",
			children: messages.map((message) => /* @__PURE__ */ jsx("li", { children: message }, message))
		});
	}, [children, errors]);
	if (!content) return null;
	return /* @__PURE__ */ jsx("div", {
		...rest,
		role: "alert",
		className: cn("tw:m-0 tw:text-xs tw:text-destructive", className),
		children: content
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroupHelp.tsx
const FormGroupHelp = ({ children, ...rest }) => {
	const formGroupContext = useFormGroupContext();
	return /* @__PURE__ */ jsx(HelpBlock, {
		state: formGroupContext.state === "warning" ? "warning" : "default",
		...rest,
		children
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroupNumberInput.tsx
const FormGroupNumberInput = ({ ref, ...props }) => {
	const { state } = useFormGroupContext();
	return /* @__PURE__ */ jsx(NumberInput, {
		...props,
		ref,
		state
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroupTextarea.tsx
const FormGroupTextarea = (props) => {
	const { state } = useFormGroupContext();
	return /* @__PURE__ */ jsx(Textarea, {
		state,
		...props
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroupTextInput.tsx
const FormGroupTextInput = ({ ref, ...props }) => {
	const { state } = useFormGroupContext();
	return /* @__PURE__ */ jsx(TextInput, {
		state,
		...props,
		ref
	});
};
//#endregion
//#region src/blocks/FormGroup/FormGroup.tsx
/**
* Label + input + help/error text for one field, with shared validation state.
*
* - **Use:** a single field outside `useAppForm`.
* - **Don't use:** for a full form — `useAppForm` from `blocks/Form` composes `FormGroup` itself.
* - **Composition:** `Label`, one `FormGroup.*Input` / `Textarea`, then optional `Help` or `Error`.
*/
const FormGroup = ({ className, children, state, ...rest }) => {
	return /* @__PURE__ */ jsx(FormGroupProvider, {
		value: { state },
		children: /* @__PURE__ */ jsx("div", {
			...rest,
			className: cn("tw:flex tw:flex-col tw:gap-1", className),
			children
		})
	});
};
FormGroup.Help = FormGroupHelp;
FormGroup.Error = FormGroupError;
FormGroup.TextInput = FormGroupTextInput;
FormGroup.NumberInput = FormGroupNumberInput;
FormGroup.ColorInput = FormGroupColorInput;
FormGroup.Textarea = FormGroupTextarea;
//#endregion
export { FormGroup };

//# sourceMappingURL=FormGroup-DiYFdEhH.js.map